import { WEEKDAYS, weekdayInstant } from "@colossus/contracts/weekday";
import type { Interval } from "./scope.ts";
import type { Locale } from "./locale.ts";

export type ValueFormat =
  | "count"
  | "compact"
  | "percent"
  | "signed"
  | "change"
  | "seconds"
  | "minutes"
  | "days"
  | "decimal";

export interface Value {
  readonly amount: number;
  readonly format: ValueFormat;
}

export const value = (amount: number, format: ValueFormat = "count"): Value => ({ amount, format });

export interface FormatContext {
  readonly locale: Locale;
  readonly timeZone: string | null;
}

const numberFormats = new Map<string, Intl.NumberFormat>();

const numberFormat = (locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  const found = numberFormats.get(key);
  if (found !== undefined) return found;
  const created = new Intl.NumberFormat(locale, options);
  numberFormats.set(key, created);
  return created;
};

const dateFormats = new Map<string, Intl.DateTimeFormat>();

export const dateFormat = (
  context: FormatContext,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const key = `${context.locale}|${context.timeZone}|${JSON.stringify(options)}`;
  const found = dateFormats.get(key);
  if (found !== undefined) return found;
  const created = new Intl.DateTimeFormat(context.locale, {
    ...options,
    ...(context.timeZone === null ? {} : { timeZone: context.timeZone }),
  });
  dateFormats.set(key, created);
  return created;
};

const pluralRules = new Map<string, Intl.PluralRules>();

export const plural = <One, Other>(
  locale: string,
  value: number,
  forms: { readonly one: One; readonly other: Other },
): One | Other => {
  const found = pluralRules.get(locale);
  const rules = found === undefined ? new Intl.PluralRules(locale) : found;
  pluralRules.set(locale, rules);
  return rules.select(value) === "one" ? forms.one : forms.other;
};

export const count = (locale: Locale, value: number): string =>
  numberFormat(locale, { maximumFractionDigits: 0 }).format(value);

export const spanText = (locale: Locale, unit: "day" | "week", value: number): string =>
  numberFormat(locale, {
    style: "unit",
    unit,
    unitDisplay: "long",
    maximumFractionDigits: 1,
  }).format(value);

export const roughly = (locale: Locale, value: number): string =>
  numberFormat(locale, { maximumFractionDigits: 1 }).format(value);

export const unitCount = (locale: Locale, unit: Interval, value: number): string =>
  numberFormat(locale, { style: "unit", unit, unitDisplay: "short" }).format(value);

export const compactCount = (locale: Locale, value: number): string =>
  numberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(value);

const decimal = (locale: Locale, value: number, digits: number): string =>
  numberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    value,
  );

const percent = (locale: Locale, ratio: number): string =>
  numberFormat(locale, { style: "percent", maximumFractionDigits: 1 }).format(ratio);

export const wholePercent = (locale: Locale, ratio: number): string =>
  numberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(ratio);

export const signedPercent = (locale: Locale, ratio: number): string =>
  numberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
    signDisplay: "exceptZero",
  }).format(ratio);

export const signedCount = (locale: Locale, value: number): string =>
  numberFormat(locale, { maximumFractionDigits: 0, signDisplay: "exceptZero" }).format(value);

const MINUTE = 60;
const HOUR = 3600;
const YEAR = 365;

const minutes = (locale: Locale, seconds: number): string =>
  seconds > 0 && seconds < MINUTE
    ? numberFormat(locale, { style: "unit", unit: "second", maximumFractionDigits: 0 }).format(
        Math.round(seconds),
      )
    : numberFormat(locale, { style: "unit", unit: "minute", maximumFractionDigits: 0 }).format(
        Math.round(seconds / MINUTE),
      );

const duration = (locale: Locale, seconds: number): string =>
  seconds >= HOUR
    ? numberFormat(locale, { style: "unit", unit: "hour", maximumFractionDigits: 1 }).format(
        seconds / HOUR,
      )
    : minutes(locale, seconds);

const elapsedDays = (locale: Locale, days: number): string =>
  days >= YEAR
    ? numberFormat(locale, { style: "unit", unit: "year", maximumFractionDigits: 1 }).format(
        days / YEAR,
      )
    : numberFormat(locale, { style: "unit", unit: "day", maximumFractionDigits: 1 }).format(days);

const RELATIVE_UNITS = [
  { unit: "second", seconds: 1, below: MINUTE },
  { unit: "minute", seconds: MINUTE, below: HOUR },
  { unit: "hour", seconds: HOUR, below: 24 * HOUR },
  { unit: "day", seconds: 24 * HOUR, below: Number.POSITIVE_INFINITY },
] as const;

const relativeFormats = new Map<Locale, Intl.RelativeTimeFormat>();

export const relativeTime = (locale: Locale, value: string, now: number): string => {
  const seconds = (Date.parse(value) - now) / 1000;
  const step =
    RELATIVE_UNITS.find((candidate) => Math.abs(seconds) < candidate.below) ?? RELATIVE_UNITS[3];
  let format = relativeFormats.get(locale);
  if (format === undefined) {
    format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    relativeFormats.set(locale, format);
  }
  return format.format(Math.round(seconds / step.seconds), step.unit);
};

const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;

export const byteSize = (locale: Locale, bytes: number): string => {
  const step = Math.min(
    BYTE_UNITS.length - 1,
    bytes < 1024 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return numberFormat(locale, {
    style: "unit",
    unit: BYTE_UNITS[step],
    maximumFractionDigits: step === 0 ? 0 : 1,
  }).format(bytes / 1024 ** step);
};

export const formatAmount = (locale: Locale, value: Value): string => {
  switch (value.format) {
    case "count":
      return count(locale, value.amount);
    case "compact":
      return compactCount(locale, value.amount);
    case "percent":
      return percent(locale, value.amount);
    case "signed":
      return signedCount(locale, value.amount);
    case "change":
      return signedPercent(locale, value.amount);
    case "seconds":
      return duration(locale, value.amount);
    case "minutes":
      return minutes(locale, value.amount);
    case "days":
      return elapsedDays(locale, value.amount);
    case "decimal":
      return decimal(locale, value.amount, 1);
  }
};

const parseInstant = (value: string): Date =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);

const BUCKET_OPTIONS: Record<Interval, Intl.DateTimeFormatOptions> = {
  hour: { month: "short", day: "numeric", hour: "numeric" },
  day: { month: "short", day: "numeric" },
  week: { month: "short", day: "numeric" },
  month: { year: "numeric", month: "short" },
};

const wallClock = (value: string): Date =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : `${value}Z`);

export const bucketLabel = (locale: Locale, value: string, interval: Interval): string =>
  dateFormat({ locale, timeZone: "UTC" }, BUCKET_OPTIONS[interval]).format(wallClock(value));

export const dayLabel = (context: FormatContext, value: string): string =>
  dateFormat(context, { year: "numeric", month: "short", day: "numeric" }).format(
    parseInstant(value),
  );

export const timestampLabel = (context: FormatContext, value: string): string =>
  dateFormat(context, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parseInstant(value));

const labelSets = new Map<string, readonly string[]>();

const labelSet = (key: string, labels: () => readonly string[]): readonly string[] => {
  const found = labelSets.get(key);
  if (found !== undefined) return found;
  const created = labels();
  labelSets.set(key, created);
  return created;
};

export const weekdayLabels = (context: FormatContext): readonly string[] =>
  labelSet(`${context.locale}|weekdays`, () => {
    const format = dateFormat({ ...context, timeZone: "UTC" }, { weekday: "short" });
    return WEEKDAYS.map((weekday) => format.format(weekdayInstant(weekday)));
  });

const hoursOfDay = (
  context: FormatContext,
  options: Intl.DateTimeFormatOptions,
): readonly string[] =>
  labelSet(`${context.locale}|${JSON.stringify(options)}`, () => {
    const format = dateFormat({ ...context, timeZone: "UTC" }, options);
    return Array.from({ length: 24 }, (_unused, hour) =>
      format.format(new Date(Date.UTC(2024, 0, 7, hour))),
    );
  });

export const hourLabels = (context: FormatContext): readonly string[] =>
  hoursOfDay(context, { hour: "numeric" });

export const clockLabels = (context: FormatContext): readonly string[] =>
  hoursOfDay(context, { hour: "numeric", minute: "2-digit" });

const utc = (locale: Locale): FormatContext => ({ locale, timeZone: "UTC" });

export const dayRange = (locale: Locale, since: string, until: string): string =>
  dateFormat(utc(locale), { year: "numeric", month: "short", day: "numeric" }).formatRange(
    parseInstant(since),
    parseInstant(until),
  );

export const shortWeekdayLabels = (locale: Locale): readonly string[] => {
  const format = dateFormat(utc(locale), { weekday: "short" });
  return Array.from({ length: 7 }, (_unused, index) =>
    [...format.format(new Date(Date.UTC(2024, 0, 7 + index)))].slice(0, 2).join(""),
  );
};

export const monthLabels = (locale: Locale): readonly string[] => {
  const format = dateFormat(utc(locale), { month: "short" });
  return Array.from({ length: 12 }, (_unused, month) =>
    format.format(new Date(Date.UTC(2024, month, 15))),
  );
};

export const monthYearLabel = (locale: Locale, year: number, month: number): string =>
  dateFormat(utc(locale), { year: "numeric", month: "long" }).format(
    new Date(Date.UTC(year, month, 15)),
  );

export const compactDayLabel = (locale: Locale, day: string, today: string): string =>
  dateFormat(
    utc(locale),
    day.slice(0, 4) === today.slice(0, 4)
      ? { month: "short", day: "numeric" }
      : { year: "numeric", month: "short", day: "numeric" },
  ).format(parseInstant(day));

const COMPACT_FROM = 10_000;

const MOST_AXIS_DIGITS = 3;

const DURATION_UNITS = [
  ["hour", HOUR],
  ["minute", MINUTE],
  ["second", 1],
] as const;

export const axisLabels = (
  locale: Locale,
  ticks: readonly number[],
  format: ValueFormat,
): readonly string[] => {
  const largest = Math.max(0, ...ticks.map((tick) => Math.abs(tick)));
  const firstDistinct = (
    candidates: readonly (readonly string[])[],
  ): readonly string[] | undefined =>
    candidates.find((labels) => new Set(labels).size === labels.length);
  if ((format === "count" || format === "signed") && largest >= COMPACT_FROM) {
    const compact = [1, 2, MOST_AXIS_DIGITS].map((digits) =>
      ticks.map((tick) =>
        numberFormat(locale, { notation: "compact", maximumFractionDigits: digits }).format(tick),
      ),
    );
    return (
      firstDistinct(compact) ?? ticks.map((tick) => formatAmount(locale, { amount: tick, format }))
    );
  }
  if (format === "seconds") {
    const units = DURATION_UNITS.filter(([, per], index) => largest >= per || index === 2);
    const written = units.map(([unit, per]) =>
      ticks.map((tick) =>
        numberFormat(locale, { style: "unit", unit, maximumFractionDigits: 1 }).format(tick / per),
      ),
    );
    return firstDistinct(written) ?? written.at(-1) ?? [];
  }
  if (format === "decimal") {
    const exact = (digits: number): boolean =>
      ticks.every((tick) => Math.abs(Math.round(tick * 10 ** digits) - tick * 10 ** digits) < 1e-6);
    const digits = [1, 2].find(exact) ?? MOST_AXIS_DIGITS;
    return ticks.map((tick) => decimal(locale, tick, digits));
  }
  return ticks.map((tick) => formatAmount(locale, { amount: tick, format }));
};

export const capitalised = (text: string, locale: Locale): string =>
  text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
